import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Table, message } from 'antd';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Search } from 'lucide-react';
import moment from 'moment';
import { useIntl } from 'react-intl';
import PaginationTable from '../../../components/pagination/Pagination';
import PopupContext from '../../../components/popupContext';
import {
  actionGetTasks,
  getTotalStatusTask,
  actionCreateSession,
  actionDeleteTasks,
  actionReassignTasks,
} from '../StudyListAction';
import {
  TASK_STATUS,
  SESSION_TYPE,
  STUDY_TABS,
  USER_ROLES,
} from '../../../utils/constants/config';
import './Task.scss';
import { isEmpty } from '../../../utils/helpers';
import { isProjectOwner } from '../../system/systemAction';
import cookie from 'js-cookie';
import { TOKEN } from '../../../utils/constants/config';

let params = { _offset: 0, _limit: 25 };

// Figma — Sidebar status filter colors
const STATUS_DOT_COLORS = {
  [TASK_STATUS.ALL]: '#2a5f5a',
  [TASK_STATUS.NEW]: '#60a5fa',
  [TASK_STATUS.DOING]: '#fbbf24',
  [TASK_STATUS.COMPLETED]: '#34d399',
};

const STATUS_ACTIVE_STYLES = {
  [TASK_STATUS.ALL]: {
    background: 'rgba(42,95,90,0.1)',
    border: '1px solid rgba(42,95,90,0.3)',
    color: '#2a5f5a',
  },
  [TASK_STATUS.NEW]: {
    background: 'rgba(59,130,246,0.1)',
    border: '1px solid rgba(59,130,246,0.3)',
    color: '#60a5fa',
  },
  [TASK_STATUS.DOING]: {
    background: 'rgba(245,158,11,0.1)',
    border: '1px solid rgba(245,158,11,0.3)',
    color: '#fbbf24',
  },
  [TASK_STATUS.COMPLETED]: {
    background: 'rgba(16,185,129,0.1)',
    border: '1px solid rgba(16,185,129,0.3)',
    color: '#34d399',
  },
};

// Figma — Type column badges
const TYPE_BADGE_STYLES = {
  ANNOTATE: {
    background: 'rgba(168,85,247,0.2)',
    color: '#d8b4fe',
    border: '1px solid rgba(168,85,247,0.3)',
  },
  REVIEW: {
    background: 'rgba(59,130,246,0.2)',
    color: '#93c5fd',
    border: '1px solid rgba(59,130,246,0.3)',
  },
};

// Figma — Status column badges
const TASK_STATUS_BADGE_STYLES = {
  NEW: {
    background: 'rgba(59,130,246,0.3)',
    color: '#93c5fd',
    border: '1px solid rgba(59,130,246,0.4)',
  },
  DOING: {
    background: 'rgba(245,158,11,0.3)',
    color: '#fcd34d',
    border: '1px solid rgba(245,158,11,0.4)',
  },
  COMPLETED: {
    background: 'rgba(16,185,129,0.3)',
    color: '#6ee7b7',
    border: '1px solid rgba(16,185,129,0.4)',
  },
};

const Task = (props) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRowItems, setSelectedRowItems] = useState([]);
  const [totalStatus, setTotalStatus] = useState({});
  const [labelers, setLabelers] = useState([]);
  const [selectedLabeler, setSelectedLabeler] = useState({});
  const [selectedStatus, setSelectedStatus] = useState(TASK_STATUS.ALL);
  const [processing, setProcessing] = useState(false);
  const { formatMessage: t } = useIntl();
  const [popupContext, setPopupContext] = useState({ visible: false });
  const {
    tasks,
    isFetchingTask,
    currentProject,
    projectId = '',
    typeTask,
    userInfo,
    viewMode,
  } = props;

  const isPO = isProjectOwner(currentProject, userInfo);
  const isViewAsPO = viewMode === USER_ROLES.PROJECT_OWNER;
  const refSearch = useRef('');

  useEffect(() => {
    return () => {
      params = { _offset: 0, _limit: 25 };
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (projectId && viewMode) {
      params = {
        _offset: 0,
        _limit: 25,
        project_id: projectId,
      };

      if (refSearch.current) {
        refSearch.current.value = '';
      }
      setSelectedLabeler({});
      setSelectedStatus(TASK_STATUS.ALL);

      if (typeTask === STUDY_TABS.TASK) {
        params._role = USER_ROLES.PROJECT_OWNER;
      } else if (typeTask === STUDY_TABS.ANNOTATE) {
        params._role = USER_ROLES.ANNOTATOR;
        params.type = STUDY_TABS.ANNOTATE;
      } else {
        params._role = USER_ROLES.REVIEWER;
        params.type = STUDY_TABS.REVIEW;
      }

      handleFetchData(params);
      handleGetTotalStatus({ ...params, _offset: 0, _limit: 0 });
    }
    // eslint-disable-next-line
  }, [projectId, typeTask, viewMode]);

  const handleFetchData = (newParams = {}) => {
    props.actionGetTasks({
      ...newParams,
      _offset: newParams._offset * newParams._limit,
    });
  };

  const handleGetTotalStatus = async (newParams = {}) => {
    try {
      const { data = {} } = await getTotalStatusTask(newParams);
      const { agg = {} } = data;
      const { assignee_id = {} } = agg;

      if (!isEmpty(assignee_id)) {
        const result = Object.keys(assignee_id).map((usrId) => ({
          id: usrId,
          name: getUsername(usrId),
          total: assignee_id[usrId],
        }));
        setLabelers(result || []);
      }

      setTotalStatus(data);
    } catch (error) {
      console.log(error);
    }
  };

  const getUsername = (usrId = '') => {
    if (currentProject) {
      let userInfo = (currentProject?.people || []).find(
        (it) => it.id === usrId
      );
      return (userInfo || {}).username || usrId;
    } else {
      return usrId;
    }
  };

  const taskStatus = useMemo(() => {
    return [
      {
        id: TASK_STATUS.ALL,
        name: t({ id: 'IDS_ALL' }),
        total: totalStatus?.[TASK_STATUS.ALL] || 0,
      },
      {
        id: TASK_STATUS.NEW,
        name: t({ id: 'IDS_NEW' }),
        total: totalStatus?.agg?.status?.[TASK_STATUS.NEW] || 0,
      },
      {
        id: TASK_STATUS.DOING,
        name: t({ id: 'IDS_DOING' }),
        total: totalStatus?.agg?.status?.[TASK_STATUS.DOING] || 0,
      },
      {
        id: TASK_STATUS.COMPLETED,
        name: t({ id: 'IDS_COMPLETED' }),
        total: totalStatus?.agg?.status?.[TASK_STATUS.COMPLETED] || 0,
      },
    ];
  }, [totalStatus, t]);

  const columns = [
    {
      title: t({ id: 'IDS_TASK_CODE' }),
      dataIndex: 'code',
      key: 'code',
      ellipsis: true,
    },
    {
      title: t({ id: 'IDS_ASSIGNEE' }),
      dataIndex: 'assignee_id',
      key: 'assignee_id',
      ellipsis: true,
      render: (usrId) => <span>{getUsername(usrId)}</span>,
    },
    {
      title: t({ id: 'IDS_TYPE' }),
      dataIndex: 'type',
      key: 'type',
      ellipsis: true,
      width: 140,
      align: 'center',
      render: (type) => (
        <span
          className="task-type-badge"
          style={TYPE_BADGE_STYLES[type] || {}}
        >
          {type}
        </span>
      ),
    },
    {
      title: t({ id: 'IDS_STUDY_CODE' }),
      dataIndex: 'studyCode',
      key: 'studyCode',
      ellipsis: true,
      render: (_, record) => <span>{record?.study?.code}</span>,
    },
    {
      title: t({ id: 'IDS_LAST_ACTIVITY' }),
      dataIndex: 'modified',
      key: 'modified',
      sorter: true,
      ellipsis: true,
      showSorterTooltip: false,
      width: 220,
      align: 'center',
      render: (txt) => (
        <span>{txt ? moment(txt).format('YYYY-MM-DD HH:mm') : ''}</span>
      ),
    },
    {
      title: t({ id: 'IDS_ARCHIVED' }),
      dataIndex: 'archived',
      key: 'archived',
      width: 120,
      align: 'center',
      render: (archived) => (
        <span className="task-archived">
          {archived ? t({ id: 'IDS_ARCHIVED' }) : ''}
        </span>
      ),
    },
    {
      title: t({ id: 'IDS_STATUS' }),
      dataIndex: 'status',
      key: 'status',
      width: 160,
      align: 'center',
      render: (status) => (
        <span
          className="task-status-badge"
          style={TASK_STATUS_BADGE_STYLES[status?.toUpperCase()] || {}}
        >
          {status}
        </span>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (rowKeys, rowItems) => {
      setSelectedRowKeys(rowKeys);
      setSelectedRowItems(rowItems);
    },
  };

  const resetSelectedItem = () => {
    setSelectedRowKeys([]);
    setSelectedRowItems([]);
  };

  const handleCreateSession = async (task) => {
    if (processing) return;
    try {
      let listStudyUID = [];
      if (task) {
        const studyInstanceUID =
          (task?.study?.dicom_tags?.StudyInstanceUID || [])[0] || '';
        listStudyUID = [
          {
            type: SESSION_TYPE.TASK,
            id: studyInstanceUID,
            meta: {
              project_id: task?.project_id,
              study_code: task?.code,
              study_id: task?.study?.id,
              project_name: currentProject?.name || '',
              task_id: task?.id,
            },
          },
        ];
      } else {
        listStudyUID = selectedRowItems.map((item) => ({
          type: SESSION_TYPE.TASK,
          id: (item?.study?.dicom_tags?.StudyInstanceUID || [])[0] || '',
          meta: {
            project_id: item?.project_id,
            study_code: item?.code,
            study_id: item?.study?.id,
            project_name: currentProject?.name || '',
            task_id: item?.id,
          },
        }));
      }

      setProcessing(true);
      const { data } = await actionCreateSession({ data: listStudyUID });
      const { session_id } = data?.data || {};
      if (session_id) {
        // Push each study from clinical Orthanc → annotation Orthanc BEFORE opening viewer.
        // We must await the push so annotation Orthanc has the study ready when viewer loads.
        const studyUids = [...new Set(listStudyUID.map((s) => s.id).filter(Boolean))];
        try {
          await Promise.all(
            studyUids.map((uid) =>
              fetch('/push-study', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  // Multi-tenant: wado-proxy reads hospital_id from this JWT
                  // to pick the correct per-hospital clinical Orthanc.
                  Authorization: `Bearer ${cookie.get(TOKEN) || ''}`,
                },
                body: JSON.stringify({ study_uid: uid }),
              }).then((r) => r.json()).catch(() => ({}))
            )
          );
        } catch (_) {
          // If push fails, open anyway — images may not load but don't block the user
        }
        const studyUID = studyUids[0] || '';
        window.open(
          window.origin + '/afya-annotation-viewer/viewer?StudyInstanceUIDs=' + studyUID + '&sessionId=' + session_id + '&time=' + Date.now(),
          '_blank'
        );
      }
      setProcessing(false);
    } catch (error) {
      console.log(error);
      setProcessing(false);
      message.error('Error!');
    }
  };

  const handleOpenAnnotationV3 = async () => {
    if (processing || selectedRowKeys.length === 0) return;
    try {
      const listStudyUID = selectedRowItems.map((item) => ({
        type: SESSION_TYPE.TASK,
        id: (item?.study?.dicom_tags?.StudyInstanceUID || [])[0] || '',
        meta: {
          project_id: item?.project_id,
          study_code: item?.code,
          study_id: item?.study?.id,
          project_name: currentProject?.name || '',
          task_id: item?.id,
        },
      }));
      setProcessing(true);
      const { data } = await actionCreateSession({ data: listStudyUID });
      setProcessing(false);
      const { session_id } = data?.data || {};
      if (session_id) {
        const studyUID = listStudyUID[0]?.id || '';
        window.open(
          window.origin + '/afya-annotation-viewer/viewer?StudyInstanceUIDs=' + studyUID + '&sessionId=' + session_id + '&time=' + Date.now(),
          '_blank'
        );
      }
    } catch (error) {
      console.log(error);
      setProcessing(false);
      message.error('Error!');
    }
  };

  const onChangePagination = (page, size) => {
    params = { ...params, _offset: page - 1, _limit: size };
    resetSelectedItem();
    handleFetchData(params);
  };

  const handleTableChange = (_, filters, sorter) => {
    if (sorter?.order) {
      params._sort =
        sorter?.order === 'ascend' ? sorter.field : `-${sorter.field}`;
    } else {
      delete params._sort;
    }
    resetSelectedItem();
    handleFetchData(params);
  };

  const handleSearch = (e) => {
    if (e.keyCode === 13) {
      const value = e.target.value;
      if (value.trim()) {
        params = { ...params, _search: value.trim() };
      } else {
        delete params._search;
      }
      params._offset = 0;
      resetSelectedItem();
      handleFetchData(params);
    }
  };

  const handleActionReassign = async () => {
    if (processing || selectedRowKeys.length === 0) return;
    try {
      setProcessing(true);
      await actionReassignTasks({
        ids: selectedRowKeys,
        status: TASK_STATUS.NEW,
      });
      setProcessing(false);
      handleFetchData(params);
      resetSelectedItem();
    } catch (error) {
      console.log(error);
      message.error('Error');
      setProcessing(false);
    }
  };

  const handleActionUnassign = async () => {
    if (processing || selectedRowKeys.length === 0) return;
    try {
      setProcessing(true);
      await actionDeleteTasks(selectedRowKeys);
      params._offset = 0;
      handleFetchData(params);
      resetSelectedItem();
      setProcessing(false);
    } catch (error) {
      console.log(error);
      message.error('Error');
      setProcessing(false);
    }
  };

  // Unused button actions kept in code for future re-enablement:
  // v3 Viewer  → /afya-viewer/viewer?StudyInstanceUIDs=...
  // v3 Seg     → /afya-viewer/segmentation?StudyInstanceUIDs=...
  // handleOpenAnnotationV3 — same as Open but uses selectedRowItems directly

  const buttonList = [
    {
      id: 1,
      name: t({ id: 'IDS_OPEN' }),
      isDisable: selectedRowKeys?.length === 0,
      // Opens AfyaNX Annotation Viewer (OHIF v3) for the selected task(s)
      action: handleOpenAnnotationV3,
    },
  ];

  if (isViewAsPO && isPO) {
    buttonList.push({
      id: 2,
      name: t({ id: 'IDS_REASSIGN' }),
      isDisable: selectedRowKeys?.length === 0,
      action: handleActionReassign,
    });
    buttonList.push({
      id: 3,
      name: t({ id: 'IDS_UNASSIGN' }),
      isDisable: selectedRowKeys?.length === 0,
      danger: true,
      action: handleActionUnassign,
    });
  }

  const onFilterStatus = (status = {}) => {
    if (selectedStatus === status.id) return;
    setSelectedStatus(status.id);
    params._offset = 0;
    if (status.id === TASK_STATUS.ALL) {
      delete params.status;
    } else {
      params.status = status.id;
    }
    resetSelectedItem();
    handleFetchData(params);
  };

  return (
    <div className="task-page">
      {/* Action Buttons — outside table, on main page background */}
      <div className="outer-action-bar">
        <div className="outer-action-left">
          {selectedRowKeys.length > 0 &&
            buttonList.map((btn) => (
              <button
                key={btn.id}
                className={`btn-action ${
                  btn.danger ? 'btn-danger' : 'btn-teal'
                }`}
                disabled={btn.isDisable}
                onClick={() => btn.action()}
              >
                {btn.name} ({selectedRowKeys.length})
              </button>
            ))}
        </div>
      </div>

      <div className="task-data-container">
        {/* Left Sidebar */}
        <div className="sidebar-panel">
          {/* Status Panel */}
          <div className="sidebar-card">
            <div className="card-title">{t({ id: 'IDS_STATUS' })}</div>
            <div className="status-list">
              {taskStatus.map((it) => {
                const isActive = selectedStatus === it.id;
                const dotColor = STATUS_DOT_COLORS[it.id] || '#2a5f5a';
                const activeStyle = isActive
                  ? STATUS_ACTIVE_STYLES[it.id] || {}
                  : {};
                return (
                  <div
                    className={`status-item ${isActive ? 'is-active' : ''}`}
                    key={it.id}
                    style={{
                      ...activeStyle,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '8px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      transition: 'all 0.2s',
                    }}
                    onClick={() => onFilterStatus(it)}
                  >
                    <span
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: dotColor,
                        marginRight: '10px',
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        flex: 1,
                        color: isActive
                          ? activeStyle.color || '#2a5f5a'
                          : '#9ca3af',
                      }}
                    >
                      {it.name}
                    </span>
                    <span
                      style={{
                        fontWeight: 600,
                        color: isActive
                          ? activeStyle.color || '#2a5f5a'
                          : '#9ca3af',
                      }}
                    >
                      {it.total}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Labelers Panel */}
          {isViewAsPO && isPO && (
            <div className="sidebar-card">
              <div className="card-title">{t({ id: 'IDS_TASK_LABELERS' })}</div>
              <div className="labeler-list">
                {labelers?.map((lb) => (
                  <div
                    className={`labeler-item ${
                      selectedLabeler.id === lb.id ? 'is-active' : ''
                    }`}
                    key={lb.id}
                    onClick={() => {
                      params._offset = 0;
                      if (selectedLabeler.id === lb.id) {
                        setSelectedLabeler({});
                        delete params.assignee_id;
                      } else {
                        setSelectedLabeler(lb);
                        params.assignee_id = lb.id;
                      }
                      resetSelectedItem();
                      handleFetchData(params);
                    }}
                  >
                    <span className="labeler-name">{lb.name}</span>
                    <span className="labeler-count">{lb.total}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Table Panel */}
        <div className="table-panel">
          {/* Table Controls */}
          <div className="table-controls">
            <div className="controls-left">
              <div className="search-wrapper">
                <Search size={14} color="#9ca3af" />
                <input
                  className="search-input"
                  placeholder={t({ id: 'IDS_SEARCH' })}
                  onKeyDown={handleSearch}
                  ref={refSearch}
                />
              </div>
              <div className="select-info">
                <span className="select-label">Select:</span>
                <span
                  className={`select-count ${
                    selectedRowKeys.length > 0 ? 'active' : ''
                  }`}
                >
                  {selectedRowKeys.length || 0}
                </span>
                <span className="select-total">
                  {' '}
                  / {tasks?.count || 0}
                </span>
              </div>
            </div>
            <div className="controls-right">
              <span className="rows-label">Rows:</span>
              <PaginationTable
                page={params._offset}
                size={params._limit}
                onChange={onChangePagination}
                totalElements={tasks.count || 0}
                defaultPageSize={params._limit}
                onChangePageSize={(value) => {
                  params._offset = 0;
                  params._limit = value;
                  resetSelectedItem();
                  handleFetchData(params);
                }}
              />
            </div>
          </div>

          <div className="table-body-wrapper">
          <Table
            className="figma-task-table"
            size="small"
            loading={isFetchingTask}
            rowKey={(record) => record.id}
            rowSelection={rowSelection}
            dataSource={tasks.data || []}
            columns={columns}
            pagination={false}
            /* Scroll is handled by the surrounding `.table-body-wrapper`
             * (see Task.scss) — using the wrapper instead of Antd's own
             * `scroll.y` because this Task component is re-used for both
             * Annotate and Review filter tabs with the existing wrapper
             * already in place.  Constraining the wrapper means both
             * tabs and the sticky-header behaviour land in one spot. */
            onChange={handleTableChange}
            onRow={(record) => {
              return {
                onContextMenu: (event) => {
                  event.preventDefault();
                  if (selectedRowKeys.length === 0) return;

                  document.addEventListener(`click`, function onClickOutside() {
                    setPopupContext({
                      visible: false,
                    });
                    document.removeEventListener(`click`, onClickOutside);
                  });
                  setPopupContext({
                    visible: true,
                    x: event.clientX,
                    y: event.clientY,
                  });
                },
                onDoubleClick: (event) => {
                  event.stopPropagation();
                  event.preventDefault();
                  if (event?._targetInst?.elementType === 'input') {
                    return;
                  }
                  handleCreateSession(record);
                },
              };
            }}
          />
          </div>

          {/* Pagination at bottom of table container */}
          {tasks?.count > 0 && (
            <div className="table-pagination">
              <span className="pagination-info">
                Showing{' '}
                <span className="pagination-bold">
                  {params._offset * params._limit + 1}
                </span>{' '}
                to{' '}
                <span className="pagination-bold">
                  {Math.min(
                    (params._offset + 1) * params._limit,
                    tasks.count
                  )}
                </span>{' '}
                of{' '}
                <span className="pagination-bold">{tasks.count}</span>
              </span>
              <div className="pagination-controls">
                <button
                  className="pagination-btn"
                  disabled={params._offset === 0}
                  onClick={() =>
                    onChangePagination(params._offset, params._limit)
                  }
                >
                  &#8249;
                </button>
                <span className="pagination-page">
                  Page {params._offset + 1} of{' '}
                  {Math.ceil(tasks.count / params._limit) || 1}
                </span>
                <button
                  className="pagination-btn"
                  disabled={
                    (params._offset + 1) * params._limit >= tasks.count
                  }
                  onClick={() =>
                    onChangePagination(params._offset + 2, params._limit)
                  }
                >
                  &#8250;
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <PopupContext buttonList={buttonList} {...popupContext} />
    </div>
  );
};

export default connect(
  (state) => ({
    tasks: state.study.tasks,
    isFetchingTask: state.study.isFetchingTask,
    currentProject: state.project.currentProject,
    userInfo: state.system.profile,
    viewMode: state.system.viewMode,
  }),
  { actionGetTasks }
)(withRouter(Task));
